<template>
    <div ref="scrollContainer" class="library-header-actions">
        <div class="library-header-actions-container">
            <PublishDropdownButton
                v-if="$auth.userCan($auth.Actions.CanPublishDeliverableToAdServer, { clientId, campaignId })"
                :deliverables="selectedDeliverables"
                :item-to-change-number="numberOfSelectedDeliverables"
                :is-disabled="selectedDeliverables.length === 0 || showPsdOptions || showAEOptions"
                :show-publish-to-opendc="showPublishToOpendc"
                :last-job="lastJob"
                @refetchDeliverables="emitRefetchDeliverables"
                @publishedStatusHasBeenSet="emitBulkPublishedStatusHasBeenSet"
                @showPublishModal="emitShowPublishModal"
            />

            <SetDeliverablesVisibleToDropdown
                v-if="$auth.userCan($auth.Actions.CanChangeDeliverableVisibility, { clientId, campaignId })"
                :deliverables="selectedDeliverables"
                :item-to-change-number="numberOfSelectedDeliverables"
            />

            <SetDeliverablesStatusDropdown
                v-if="$auth.userCan($auth.Actions.CanChangeDeliverableQAStatus, { clientId, campaignId })"
                :deliverables="selectedDeliverables"
                :item-to-change-number="numberOfSelectedDeliverables"
                @statusHasBeenSet="emitBulkQaStatusHasBeenSet"
            />

            <Divider type="vertical" class="divider" />

            <Tooltip
                v-if="hasCreativeXEnabled || hasSataliaEnabled"
                transfer-class-name="library-header__tooltip"
                placement="top"
                :transfer="true"
                max-width="200"
                :disabled="!sendToPreflightButtonTooltip"
            >
                <Button
                    data-testid="library-header__pre-flight-button"
                    icon="md-send"
                    :disabled="isSendToPreflightButtonDisabled"
                    :loading="isSendingToPreflight"
                    @click="sendToPreFlight"
                >
                    Send to pre-flight
                </Button>
                <template #content>
                    {{ sendToPreflightButtonTooltip }}
                </template>
            </Tooltip>

            <Button
                v-if="$auth.userCan($auth.Actions.CanShareDeliverable, { clientId, campaignId })"
                :disabled="!hasDeliverablesSelected || showAEOptions"
                icon="ios-link"
                data-testid="library-header__share-button"
                @click="showSharePreviewModal"
            >
                Share
            </Button>

            <Button
                :type="bulkApprovaButtonType"
                :disabled="!hasDeliverablesSelected"
                icon="ios-text-outline"
                data-testid="library-header__review-button"
                @click="showBulkApproval"
            >
                Review
            </Button>

            <Button
                v-if="$auth.userCan($auth.Actions.CanDownloadDeliverable, { clientId, campaignId })"
                :disabled="!hasDeliverablesSelected"
                icon="md-download"
                data-testid="library-header__download-button"
                @click="showDownloadModal"
            >
                Download
            </Button>
        </div>
    </div>
</template>

<script lang="ts">
import { type PropType, defineComponent } from "vue";

import type { ConstructedDeliverable } from "@/types/types";

import { PLATFORM } from "hoxton-shared/enums/exports";

import PublishDropdownButton from "@/components/Campaign/Publish/PublishDropdownButton.vue";
import SetDeliverablesStatusDropdown from "@/components/Campaign/SetDeliverablesStatusDropdown.vue";
import SetDeliverablesVisibleToDropdown from "@/components/Campaign/SetDeliverablesVisibleToDropdown.vue";

import { publishDeliverables } from "@/services/deliverables/publish";

import { JobsAction } from "@/store/modules/jobs";
import { NotificationsAction } from "@/store/modules/notifications";

import { JobStatus } from "@/enums/jobs";
import { NotificationStatus, NotificationTypes } from "@/enums/notifications";
import { useGA4Tracking } from "@/composables/useGA4Tracking";
import { GA4_EVENT_TYPES, GA4_FEATURE_CATEGORIES } from "@/enums/ga4Events";

type PlatformType = (typeof PLATFORM)[keyof typeof PLATFORM];

import { mapStores } from "pinia";
import { useCreativeLibraryFiltersStore } from "@/store/pinia/creativeLibraryFilters";

export default defineComponent({
    name: "LibraryActionsHeader",
    components: {
        PublishDropdownButton,
        SetDeliverablesStatusDropdown,
        SetDeliverablesVisibleToDropdown
    },

    props: {
        selectedDeliverables: {
            required: true,
            type: Array as PropType<ConstructedDeliverable[]>
        },
        totalItems: {
            required: true,
            type: Number
        },
        showAEOptions: {
            required: true,
            type: Boolean
        },
        showPsdOptions: {
            required: true,
            type: Boolean
        },
        lastJob: {
            required: false,
            type: Object,
            default: null
        },
        campaignFeed: {
            required: true,
            type: Array
        }
    },

    emits: [
        "bulkPublishedStatusHasBeenSet",
        "bulkQaStatusHasBeenSet",
        "showPublishModal",
        "showSharePreviewModal",
        "showBulkApproval",
        "showDownloadModal",
        "refetchDeliverables"
    ],

    setup() {
        const ga4 = useGA4Tracking();

        return {
            ga4
        };
    },

    data() {
        return {
            isSendingToPreflight: false
        };
    },

    computed: {
        ...mapStores(useCreativeLibraryFiltersStore),

        bulkApprovaButtonType() {
            return this.$auth.userCan(this.$auth.Actions.CanChangeDeliverableQAStatus, {
                clientId: this.clientId,
                campaignId: this.campaignId
            })
                ? "default"
                : "primary";
        },

        isLastJobComplete() {
            return this.lastJob?.status === JobStatus.COMPLETE;
        },

        isSendToPreflightButtonDisabled() {
            const hasValidConfiguration = this.hasSataliaEnabled || this.hasCreativeXConfigured;
            return (
                !hasValidConfiguration ||
                !this.hasDeliverablesSelected ||
                this.isSendingToPreflight ||
                this.isCampaignLocked
            );
        },

        showPublishToOpendc() {
            return (
                this.isLastJobComplete &&
                this.checkDeliveriesMatchFeed(this.selectedDeliverables, this.campaignFeed) &&
                !!this.$store.state.campaign.opendcPlatformSettings?.campaignType
            );
        },

        campaignId() {
            return this.$route.params.campaignId as string;
        },

        clientId() {
            return this.$route.params.clientId as string;
        },

        hasDeliverablesSelected() {
            return this.selectedDeliverables.length > 0;
        },

        numberOfSelectedDeliverables() {
            if (this.creativeLibraryFiltersStore.allSelectedMode) {
                return this.totalItems;
            }

            return this.selectedDeliverables.length;
        },

        isCampaignLocked() {
            return this.$store.state.campaign.isLocked;
        },

        hasCreativeXEnabled() {
            return this.$store.state.ui.currentClient?.creativeXPlatformSettings?.enabled ?? false;
        },

        hasCreativeXConfigured() {
            const settings = this.$store.state.campaign.creativeXPlatformSettings;
            return !!(settings?.marketCode && settings?.channel);
        },

        sendToPreflightButtonTooltip() {
            if (this.isCampaignLocked) {
                return "Campaign is locked";
            }

            const hasValidConfiguration = this.hasSataliaEnabled || this.hasCreativeXConfigured;
            if (!hasValidConfiguration) {
                if (this.hasCreativeXEnabled && !this.hasCreativeXConfigured) {
                    return "Admin has to configure CreativeX settings in campaign. Edit to enable pre-flight";
                }
                if (this.hasSataliaEnabled) {
                    return "Admin has to configure Pre-flight settings in campaign. Edit to enable pre-flight";
                }
                return "Pre-flight not configured for this campaign";
            }

            if (!this.hasDeliverablesSelected) {
                return "Select at least one deliverable to send to pre-flight";
            }

            if (this.isSendingToPreflight) {
                return "Sending to pre-flight...";
            }

            return "";
        },

        hasSataliaEnabled() {
            return this.$store.state.ui.currentClient?.sataliaPlatformSettings?.enabled ?? false;
        }
    },

    mounted() {
        (this.$refs.scrollContainer as HTMLElement).addEventListener("wheel", this.handleWheel, { passive: false });
    },
    beforeUnmount() {
        (this.$refs.scrollContainer as HTMLElement).removeEventListener("wheel", this.handleWheel);
    },

    methods: {
        handleWheel(event: WheelEvent) {
            if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
                event.preventDefault();
                (this.$refs.scrollContainer as HTMLElement).scrollLeft += event.deltaY;
            }
        },

        emitRefetchDeliverables() {
            this.$emit("refetchDeliverables");
        },

        emitBulkPublishedStatusHasBeenSet() {
            this.$emit("bulkPublishedStatusHasBeenSet");
        },

        emitBulkQaStatusHasBeenSet() {
            this.$emit("bulkQaStatusHasBeenSet");
        },

        emitShowPublishModal(platformAction: { platform: PlatformType; action: "publish" | "unpublish" | "rebuild" }) {
            this.$emit("showPublishModal", platformAction);
        },

        showBulkApproval() {
            this.$emit("showBulkApproval");
        },

        showSharePreviewModal() {
            this.$emit("showSharePreviewModal");
        },

        async sendToPreFlight() {
            if (this.selectedDeliverables.length === 0) {
                return;
            }

            const platform = this.hasSataliaEnabled ? PLATFORM.SATALIA : PLATFORM.CREATIVEX;
            const platformName = this.hasSataliaEnabled ? "Pre-flight" : "CreativeX";

            this.isSendingToPreflight = true;

            this.ga4.trackFeatureEvent({
                featureEvent: GA4_EVENT_TYPES.FEATURE_ACTIVATE,
                featureCategory: GA4_FEATURE_CATEGORIES.PREFLIGHT,
                featureContext: `Send to ${platformName}`
            });

            try {
                const job = await publishDeliverables({
                    campaignId: this.campaignId,
                    platform,
                    deliverables: this.selectedDeliverables,
                    libraryFilters: this.creativeLibraryFiltersStore.getComputedLibraryFilters,
                    isRendering: false,
                    rebuild: false
                });

                const notificationId = await this.$store.dispatch(NotificationsAction.Add, {
                    deliverablesCount: this.selectedDeliverables.length,
                    platform,
                    status: NotificationStatus.InProgress,
                    type: NotificationTypes.Preflight,
                    job
                });

                this.$store.dispatch(JobsAction.SetNotificationIdByActiveJobId, {
                    jobId: job._id,
                    notificationId
                });

                const message = this.hasSataliaEnabled
                    ? `Ads started to be sent to ${platformName}. Please check the notifications for updates.`
                    : `Ads started to be sent to ${platformName} pre-flight. Please check the notifications for updates.`;
                this.$snackbar.success(message);

                this.ga4.trackFeatureEvent({
                    featureEvent: GA4_EVENT_TYPES.FEATURE_COMPLETE,
                    featureCategory: GA4_FEATURE_CATEGORIES.PREFLIGHT,
                    featureContext: `Send to ${platformName}`
                });
            } catch (error) {
                console.error("Error sending ads to pre-flight:", error);
                this.$snackbar.error("Error sending ads to pre-flight");
            }

            this.isSendingToPreflight = false;
        },

        showDownloadModal() {
            this.$emit("showDownloadModal");
        },

        checkDeliveriesMatchFeed(selectedDeliveries, feed) {
            const feedReporting = new Set(feed.map(f => f.Unique_ID));
            const deliveryReporting = new Set(
                selectedDeliveries.map(delivery => `${delivery.reportingLabel}_${delivery.hash}`)
            );
            // Check if every item in deliveryReporting is in feedReporting
            for (const label of deliveryReporting) {
                if (!feedReporting.has(label)) {
                    return false;
                }
            }
            return true;
        }
    }
});
</script>

<style lang="scss" scoped>
@use "@style/variables" as *;

.library-header-actions {
    background-color: $white;
    padding: 10px 20px;
    border-bottom: 1px solid $grey3;
    border-left: 1px solid $grey3;
    overflow-y: auto;

    .library-header-actions-container {
        margin-left: auto;
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 5px;
        width: max-content;

        .divider {
            height: 30px;
        }
    }
}
</style>
<style lang="scss">
.library-header__tooltip {
    .ivu-tooltip-inner-with-width {
        word-break: break-word;
        text-align: left;
    }
}
</style>
